import {
  LocalModelInputPreset,
  LocalModelInputPresetEntryInputUserValue,
  LocalModelInputPresetInputUserValue,
  LocalTaskPrivacyLevels,
  LocalThirdPartyAPIKeysNames,
  LocalUserThirdPartyKeys,
  MLTasksData,
  ModelInputEntry,
  ModelInputOutputEntryType,
  ModelOutputEntry,
  ModelsInfo,
  TaskExecutionData,
  TaskExecutionInput,
  TaskExecutionOutput,
} from "@/modeltypings";
import { TaskExecutionCardProps } from "@/typings";
import { calculateQuote } from "@mltask/core/Utils/QuotaHelper";
import {
  ModelInputPreset,
  ModelInputPresetEntry,
  ModelInputPresetVersionHistory,
  Models,
} from "@mltask/graphql/Codegen/API";
import { sendGAEvent } from "@next/third-parties/google";

export default class TaskUtils {
  static isTaskOutputMediaType(taskExecutionOutput: TaskExecutionOutput) {
    let mediaTypes = [
      ModelInputOutputEntryType.URL_IMAGE,
      ModelInputOutputEntryType.URL_AUDIO,
      ModelInputOutputEntryType.URL_VIDEO,
    ];
    return taskExecutionOutput.outputs.reduce((sum, current) => {
      return sum && mediaTypes.some((y) => y == current.type);
    }, true);
  }
  static getTaskIDFromModelID(
    taskData: MLTasksData,
    modelID: string
  ): string | undefined {
    return taskData?.entries.find((entry) =>
      entry.modelsInfo.find((x) => x.id == modelID)
    )?.id;
  }
  static hasUserProvidedKeysForModel(
    modelUsesAPIKey: LocalThirdPartyAPIKeysNames[],
    userKeys: LocalUserThirdPartyKeys | undefined
  ): boolean {
    var userHasKeys = true;
    modelUsesAPIKey.forEach((usedApiKey) => {
      if (
        usedApiKey == LocalThirdPartyAPIKeysNames.FAL &&
        (userKeys?.Fal ?? "").length <= 1
      ) {
        userHasKeys = false;
      }
      if (
        usedApiKey == LocalThirdPartyAPIKeysNames.LUMA_LABS &&
        (userKeys?.lumaLabs ?? "").length <= 1
      ) {
        userHasKeys = false;
      }
      if (
        usedApiKey == LocalThirdPartyAPIKeysNames.OPEN_AI &&
        (userKeys?.openAI ?? "").length <= 1
      ) {
        userHasKeys = false;
      }
      if (
        usedApiKey == LocalThirdPartyAPIKeysNames.REPLICATE &&
        (userKeys?.replicate ?? "").length <= 1
      ) {
        userHasKeys = false;
      }
      if (
        usedApiKey == LocalThirdPartyAPIKeysNames.ANTHROPIC &&
        (userKeys?.anthropic ?? "").length <= 1
      ) {
        userHasKeys = false;
      }
      if (
        usedApiKey == LocalThirdPartyAPIKeysNames.STABILITY_AI &&
        (userKeys?.SAI ?? "").length <= 1
      ) {
        userHasKeys = false;
      }
      if (
        usedApiKey == LocalThirdPartyAPIKeysNames.NIM &&
        (userKeys?.nims ?? "").length <= 1
      ) {
        userHasKeys = false;
      }
    });

    return userHasKeys;
  }
  static calculateQuoteClient(
    modelInfo: ModelsInfo,
    userInputs: TaskExecutionInput,
    withAPI: boolean,
    activePreset: LocalModelInputPreset | undefined,
    presetInputUserValues:
      | LocalModelInputPresetEntryInputUserValue[]
      | undefined
  ): number {
    let convertedModel = {
      ...modelInfo,
      isPublic: true,
      mltaskdataID: "",
      createdAt: "",
      updatedAt: "",
    } as Models;
    let convertedInputs = Object.entries(userInputs).map(([key, data]) => data);
    let convertedPreset: ModelInputPreset = {
      id: activePreset?.presetID ?? "",
      presetName: activePreset?.presetName,
      description: activePreset?.description,
      cost: activePreset?.cost,
      usage: activePreset?.usage,
      isPublic: activePreset?.isPublic,
      isOpenSource: activePreset?.isOpenSource,
      entries: activePreset?.entries as ModelInputPresetEntry[],
      versionHistory:
        activePreset?.versionHistory as ModelInputPresetVersionHistory[],
      versionName: activePreset?.versionName,
      username: activePreset?.username,
      taskName: activePreset?.taskName,
      modelName: activePreset?.modelName,
      modelsID: activePreset?.modelsID ?? "",
      userID: activePreset?.userID ?? "",
      createdAt: activePreset?.createdAt ?? "",
      updatedAt: activePreset?.createdAt ?? "",
    };
    return calculateQuote(
      convertedModel,
      convertedInputs,
      withAPI,
      convertedPreset,
      presetInputUserValues
    );
  }
  // {
  //   id: string,
  //   presetName?: string | null,
  //   description?: string | null,
  //   cost?: number | null,
  //   usage?: number | null,
  //   isPublic?: boolean | null,
  //   isOpenSource?: boolean | null,
  //   entries?:  Array<ModelInputPresetEntry | null > | null,
  //   versionHistory?:  Array<ModelInputPresetVersionHistory | null > | null,
  //   versionName?: string | null,
  //   username?: string | null,
  //   taskName?: string | null,
  //   modelName?: string | null,
  //   modelsID: string,
  //   userID: string,
  //   createdAt: string,
  //   updatedAt: string,
  // }
  // {
  //   presetID: string;
  //   presetName: string;
  //   description: string;
  //   isPublic: boolean;
  //   isOpenSource: boolean;
  //   cost: number;
  //   versionName: string;
  //   versionHistory: LocalModelInputPresetVersionHistory[];
  //   entries: LocalModelInputPresetEntryInput[];
  //   modelsID: string;
  //   userID: string;
  //   username: string;
  //   taskName: string;
  //   modelName: string;
  //   usage: number;
  //   createdAt: string;
  // }

  static presentableTaskNameFromTaskNames(taskName: string) {
    return taskName.split("_").reduce((sum, current) => {
      return (
        sum +
        " " +
        current.charAt(0).toUpperCase() +
        current.slice(1).toLowerCase()
      );
    }, "");
  }
  static presentableCategoryNameFromCategoryNames(taskCategory: string) {
    return taskCategory.replaceAll("_", " ");
  }

  static skeletonContentTypeFromType(modelOutputEntry: ModelOutputEntry) {
    var inputType = "";
    switch (modelOutputEntry.type) {
      case "URL_IMAGE":
        inputType = "image";
        break;
      case "TEXT":
      case "NUMBER":
        inputType = "text";
        break;
      case "URL_VIDEO":
        inputType = "video";
        break;
    }
    return inputType;
  }

  static processExecutionInputItemReduce(
    inputValueData: any,
    sum: TaskExecutionInput,
    modelInputEntry: ModelInputEntry
  ) {
    if (inputValueData == null) {
      return sum;
    }
    if (inputValueData.value == null) {
      return sum;
    }
    if (inputValueData.value.length == 0) {
      return sum;
    }
    switch (modelInputEntry.type) {
      case ModelInputOutputEntryType.TEXT:
      case ModelInputOutputEntryType.URL_IMAGE:
      case ModelInputOutputEntryType.URL_AUDIO:
      case ModelInputOutputEntryType.URL_VIDEO:
        sum[modelInputEntry.backendName] = {
          type: modelInputEntry.type,
          name: modelInputEntry.backendName,
          value: inputValueData.value,
          metaData: inputValueData.metaData,
        };
        break;
      case ModelInputOutputEntryType.BOOLEAN:
        sum[modelInputEntry.backendName] = {
          type: modelInputEntry.type,
          name: modelInputEntry.backendName,
          value: Boolean(inputValueData.value),
          metaData: inputValueData.metaData,
        };
        break;
      case ModelInputOutputEntryType.NUMBER:
      case ModelInputOutputEntryType.NUMBER_INT:
      case ModelInputOutputEntryType.NUMBER_FLOAT:
        sum[modelInputEntry.backendName] = {
          type: modelInputEntry.type,
          name: modelInputEntry.backendName,
          value: +inputValueData.value,
          metaData: inputValueData.metaData,
        };
        break;
    }
  }
  static getAudioMinutes(inputs: TaskExecutionInput) {
    const fileInput = inputs.File;
    const metaData = fileInput?.metaData;
    if (metaData?.DURATION) {
      return metaData?.DURATION / 60;
    }
    return 1;
  }

  static interpolateRange(
    value: number,
    inMin: number,
    inMax: number,
    outMin: number,
    outMax: number
  ) {
    // Calculate the normalized value within the input range
    const normalizedValue = (value - inMin) / (inMax - inMin);

    // Calculate the interpolated value within the output range
    const interpolatedValue = normalizedValue * (outMax - outMin) + outMin;

    return interpolatedValue;
  }

  static buildTaskExecutionInput(
    tasks: MLTasksData,
    taskID: string,
    modelID: string,
    requiredInputValues: TaskExecutionInput
  ) {
    if (tasks.entries == undefined) return {};
    let targetTask = tasks.entries.find((task) => task.id == taskID);
    let targetModel = targetTask?.modelsInfo.find(
      (model) => model.id == modelID
    );
    if (targetTask == undefined || targetModel == undefined) return {};
    var executionInput: TaskExecutionInput = targetModel?.inputs.entries.reduce(
      (sum, modelInputEntry) => {
        const inputValueData = requiredInputValues[modelInputEntry.backendName];
        TaskUtils.processExecutionInputItemReduce(
          inputValueData,
          sum,
          modelInputEntry
        );
        return sum;
      },
      {} as TaskExecutionInput
    );
    return executionInput;
  }

  static buildTaksExecutionData(
    tasks: MLTasksData,
    taskID: string,
    modelID: string,
    taskPrivacy: LocalTaskPrivacyLevels,
    requiredInputValues: TaskExecutionInput,
    presetUserInput?: LocalModelInputPresetInputUserValue
  ) {
    let targetTask = tasks.entries.find((task) => task.id == taskID);
    let targetModel = targetTask?.modelsInfo.find(
      (model) => model.id == modelID
    );
    const executionInput: TaskExecutionInput =
      TaskUtils.buildTaskExecutionInput(
        tasks,
        taskID,
        modelID,
        requiredInputValues
      );
    const data: TaskExecutionData = {
      task: targetTask?.name ?? "NULL",
      modelID: modelID,
      model: targetModel?.name ?? "NULL",
      privacyLevel: taskPrivacy,
      inputs: executionInput,
      presetUserInput,
    };

    return data;
  }
  static submitTask(
    tasks: MLTasksData,
    taskID: string,
    modelID: string,
    taskPrivacy: LocalTaskPrivacyLevels,
    requiredInputValues: TaskExecutionInput,
    preset?: LocalModelInputPreset,
    presetInputUserValues?: LocalModelInputPresetEntryInputUserValue[],
    taskRequiredCredits?: number,
    onFileUploadProgressCallback?: (progress: any) => void,
    onModelFinishedExecution?: (output: TaskExecutionCardProps) => void,
    onModelExecutionFail?: (error: any) => void
  ): TaskExecutionData {
    const executionData: TaskExecutionData = this.buildTaksExecutionData(
      tasks,
      taskID,
      modelID,
      taskPrivacy,
      requiredInputValues,
      {
        presetID: preset?.presetID,
        presetName: preset?.presetName,
        presetCreatorName: preset?.username,
        presetCreatorUID: preset?.userID,
        presetValues: presetInputUserValues,
      }
    );
    var eventData: { [key: string]: any } = {
      event: "executeTaskButtonClicked",
      cost: taskRequiredCredits,
      taskName: executionData.task,
      taskModel: executionData.model,
    };
    if (executionData.presetUserInput) {
      eventData["presetID"] = executionData.presetUserInput!.presetID;
      eventData["presetName"] = executionData.presetUserInput!.presetName;
      eventData["presetCreatorName"] =
        executionData.presetUserInput!.presetCreatorName;
    }
    sendGAEvent(eventData);
    this.handleTaskExecutionCall(
      undefined,
      executionData,
      onFileUploadProgressCallback,
      onModelFinishedExecution,
      onModelExecutionFail
    );
    return executionData;
  }
  static async handleTaskExecutionCall(
    workspaceId: string | undefined,
    executionData: TaskExecutionData,
    progressCallback?: (progress: any) => void,
    completionCallback?: (output: TaskExecutionCardProps) => void,
    executionFail?: (error: any) => void
  ) {
    const BackendManager = (await import("@/utils/BackendManager")).default;
    BackendManager.executeTask(workspaceId, executionData, progressCallback)
      .then(completionCallback)
      .catch(executionFail);
  }
  static updateTaskFromGQLUpdates(
    oldTask: TaskExecutionCardProps,
    updatedTask: TaskExecutionCardProps
  ): [TaskExecutionCardProps, boolean] {
    const taskToUpdate = { ...oldTask! };
    if (updatedTask.taskExecutionData != null) {
      const taskExecutionData = updatedTask.taskExecutionData;
      if (taskExecutionData != null) {
        const { inputs } = taskExecutionData;
        if (inputs != null) {
          for (const key in inputs) {
            const entry = inputs[key];
            const inputEntry = taskToUpdate.taskExecutionData.inputs[key];
            inputEntry.metaData =
              entry.metaData ?? inputEntry.metaData ?? undefined;
            inputEntry.name = entry.name ?? inputEntry.name;
            inputEntry.type = entry.type ?? inputEntry.type;
            inputEntry.value = entry.value ?? inputEntry.value;
          }
        }

        const { presetUserInput } = taskExecutionData;
        if (presetUserInput != null) {
          if (taskToUpdate.taskExecutionData.presetUserInput == undefined)
            taskToUpdate.taskExecutionData.presetUserInput = presetUserInput;
          else {
            taskToUpdate.taskExecutionData.presetUserInput.presetCreatorName ??=
              presetUserInput.presetCreatorName;
            taskToUpdate.taskExecutionData.presetUserInput.presetCreatorUID ??=
              presetUserInput.presetCreatorUID;
            taskToUpdate.taskExecutionData.presetUserInput.presetID ??=
              presetUserInput.presetID;
            taskToUpdate.taskExecutionData.presetUserInput.presetName ??=
              presetUserInput.presetName;
            taskToUpdate.taskExecutionData.presetUserInput.presetValues =
              presetUserInput.presetValues;
          }
        }
      }
    }

    if (updatedTask.status != null) {
      taskToUpdate.status = updatedTask.status;
    }

    if (updatedTask.taskExecutionOutput != null) {
      taskToUpdate.taskExecutionOutput = updatedTask.taskExecutionOutput;
    }
    const finishedUpdates =
      taskToUpdate.status == "FINISHED" || taskToUpdate.status == "FAILED";
    return [taskToUpdate, finishedUpdates];
  }
}
